/**
 * Validation logic for file attachments in chat input.
 * Validates file size, MIME type, and attachment count limits.
 */

import { formatFileSize } from './chat-input-attachment.interface'
import {
  MAX_ATTACHMENTS,
  MAX_FILE_SIZE_BYTES,
  SUPPORTED_EXTENSIONS_FALLBACK_MAP,
  SUPPORTED_IMAGE_MIME_TYPE_LIST,
  SUPPORTED_MIME_TYPE_LIST,
} from './chat-input-composer.config'

type SupportedImageMimeType = (typeof SUPPORTED_IMAGE_MIME_TYPE_LIST)[number]

const supportedMimeTypeSet = new Set<string>(SUPPORTED_MIME_TYPE_LIST)

const supportedImageMimeTypeSet = new Set<string>(SUPPORTED_IMAGE_MIME_TYPE_LIST)

const isTextMimeType = (mimeType: string): boolean => mimeType.startsWith('text/')

export const isSupportedMimeType = (mimeType: string): boolean => {
  if (supportedMimeTypeSet.has(mimeType)) return true
  if (isTextMimeType(mimeType)) return true
  return false
}

export const isSupportedImageMimeType = (mimeType: string): mimeType is SupportedImageMimeType =>
  supportedImageMimeTypeSet.has(mimeType)

const getFileExtension = (filename: string): string => {
  const lastDot = filename.lastIndexOf('.')
  if (lastDot === -1) return ''
  return filename.slice(lastDot).toLowerCase()
}

const isSupportedByExtension = (filename: string): boolean => {
  const ext = getFileExtension(filename)
  return SUPPORTED_EXTENSIONS_FALLBACK_MAP[ext] ?? false
}

export const validateFileSize = (file: File): string | null => {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return `"${file.name}" exceeds the ${formatFileSize(MAX_FILE_SIZE_BYTES)} file size limit`
  }
  return null
}

export const validateFileType = (file: File): string | null => {
  if (file.type && isSupportedMimeType(file.type)) return null
  if (isSupportedByExtension(file.name)) return null
  return `"${file.name}" is not a supported file type`
}

export const validateAttachmentCount = (currentCount: number, newCount: number): string | null => {
  if (currentCount + newCount > MAX_ATTACHMENTS) {
    return `Maximum ${MAX_ATTACHMENTS} attachments allowed`
  }
  return null
}

export interface FileValidationResult {
  validFileList: File[]
  errorMessageList: string[]
}

export const validateFileList = (
  fileList: File[],
  currentAttachmentCount: number
): FileValidationResult => {
  const errorMessageList: string[] = []
  const validFileList: File[] = []

  const countError = validateAttachmentCount(currentAttachmentCount, fileList.length)
  if (countError) {
    errorMessageList.push(countError)
  }

  const remainingSlots = MAX_ATTACHMENTS - currentAttachmentCount

  for (const file of fileList) {
    if (validFileList.length >= remainingSlots) break

    const typeError = validateFileType(file)
    if (typeError) {
      errorMessageList.push(typeError)
      continue
    }

    const sizeError = validateFileSize(file)
    if (sizeError) {
      errorMessageList.push(sizeError)
      continue
    }

    validFileList.push(file)
  }

  return { validFileList, errorMessageList }
}
